import type { BoxSummary } from "@loot-os/api-contract";
import { PageTemplateView } from "@loot-os/design-system/templates/shared/PageTemplateView";
import { useQuery } from "@tanstack/react-query";
import { useSearch } from "@tanstack/react-router";

import { RequireAuth } from "../components/RequireAuth.js";
import { boxesQueryOptions } from "../lib/queries.js";
import { useTenant } from "../lib/tenant.js";
import {
  useBattleCreateContent,
  useBattleCreatePendingContent,
} from "../templates/pages/battles/create/useBattleCreateContent.js";
import { storefrontTemplateOf } from "../templates/registry.js";

/** POST /v1/battles, signed in only, laid out by the storefront template this environment chose. */
export function BattleCreatePage() {
  return (
    <RequireAuth>
      <CreateBattle />
    </RequireAuth>
  );
}

/**
 * Loads the boxes before the form mounts: the form's first picks come from them (`?boxId=`, the
 * box page's Battle link), so it starts once the list is known. A changed `boxId` remounts it.
 */
function CreateBattle() {
  const boxes = useQuery(boxesQueryOptions());
  const { boxId } = useSearch({ from: "/battles/create" });
  if (!boxes.data) return <CreateBattlePending isError={boxes.isError} error={boxes.error} />;
  return (
    <CreateBattleForm
      key={boxId ?? ""}
      boxes={boxes.data}
      initialBoxId={boxId}
      refetchBoxes={() => void boxes.refetch()}
    />
  );
}

function CreateBattlePending(props: { isError: boolean; error: unknown }) {
  const { config } = useTenant();
  return (
    <PageTemplateView
      template={storefrontTemplateOf(config).battleCreate}
      content={useBattleCreatePendingContent(props)}
    />
  );
}

function CreateBattleForm(props: {
  boxes: BoxSummary[];
  initialBoxId: string | undefined;
  refetchBoxes: () => void;
}) {
  const { config } = useTenant();
  return (
    <PageTemplateView
      template={storefrontTemplateOf(config).battleCreate}
      content={useBattleCreateContent(props)}
    />
  );
}
